import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  id?: string;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ anchorRef, isOpen, onClose, id, children }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  // Robust outside-click and ESC handler that checks both tooltip and the anchor
  useEffect(() => {
    if (!isOpen) return;

    const handlePointer = (evt: Event) => {
      try {
        const tooltipEl = elRef.current;
        const anchorEl = anchorRef && anchorRef.current ? anchorRef.current : null;
        if (!tooltipEl) return;

        // Try composedPath first (works with portals/shadow DOM)
        const anyEvt = evt as any;
        const path = typeof anyEvt.composedPath === 'function' ? anyEvt.composedPath() : undefined;
        if (path && Array.isArray(path)) {
          if (path.includes(tooltipEl) || (anchorEl && path.includes(anchorEl))) {
            return;
          }
        } else {
          const target = evt.target as Node | null;
          if (target && (tooltipEl.contains(target) || (anchorEl && anchorEl.contains(target)))) {
            return;
          }
        }

        // outside click
        // eslint-disable-next-line no-console
        console.debug('[Tooltip] outside click detected - closing');
        onClose();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Tooltip] handlePointer error', err);
        onClose();
      }
    };

    const handleKey = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        // eslint-disable-next-line no-console
        console.debug('[Tooltip] Escape pressed - closing');
        onClose();
      }
    };

    // capture phase so we see events before they are stopped
    document.addEventListener('pointerdown', handlePointer, true);
    document.addEventListener('mousedown', handlePointer, true);
    document.addEventListener('touchstart', handlePointer, true);
    document.addEventListener('click', handlePointer, true);
    document.addEventListener('keydown', handleKey, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointer, true);
      document.removeEventListener('mousedown', handlePointer, true);
      document.removeEventListener('touchstart', handlePointer, true);
      document.removeEventListener('click', handlePointer, true);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [isOpen, anchorRef, onClose]);

  useEffect(() => {
    function update() {
      const anchor = anchorRef.current;
      if (!anchor) return setPos(null);
      const rect = anchor.getBoundingClientRect();
      // place tooltip below anchor, left aligned
      setPos({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX });
    }

    if (isOpen) {
      update();
      window.addEventListener('resize', update);
      window.addEventListener('scroll', update, true);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, anchorRef]);

  // (removed duplicate keydown listener: handled in the outside-click effect above)

  if (!isOpen) return null;

  const tooltipNode = (
    <div
      id={id}
      ref={elRef}
      className={`css-tooltip css-tooltip--visible`}
      aria-hidden={!isOpen}
      style={{ position: 'absolute', top: pos ? pos.top : undefined, left: pos ? pos.left : undefined }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn-icon-only" aria-label="Close tooltip" onClick={onClose}>✕</button>
      </div>
      <div>
        {children}
      </div>
    </div>
  );

  const mount = document.getElementById('root') || document.body;
  return createPortal(tooltipNode, mount);
};

export default Tooltip;
